import React, { useEffect, useState } from "react";
import axios, { CancelToken } from "axios";
import { useTranslation } from "react-i18next";
import { isEqualWith, isNull } from "lodash";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPencil, faTimes } from "@fortawesome/pro-regular-svg-icons";
import useModal from "hooks/modals/useModal";
import { useHistory } from "react-router";

import { StatusCode } from "api/protocols";

import { GetCountry } from "api/rpc/2022-09/common/country/country";

import {
  GetCustomerTag,
  PutCustomer,
  GetCustomerAll,
  PostExistingCustomer,
  PutCustomerAddressLine,
  PostCustomerAddressLine,
  DeleteCustomerAddressLine,
  GetCustomerNotes,
  PutMergeCustomers,
  SendBirthdayRequestEmail,
} from "api/rpc/2024-04/facilityAdmin/customer/customer";
import { GetTag } from "api/rpc/2024-04/facilityAdmin/client/tags";

import { showError, showSuccess } from "redux/actions/ui";
import { IAddress, ICustomer } from "redux/reducers/models/customer";

import { useAppDispatch, useAppSelector } from "hooks/redux";
import { useWindowSize } from "hooks/useWindowSize/useWindowSize";
import { MOBILE_WIDTH } from "helpers/ScreenSizes";
import { unsavedChangesExist } from "helpers/Helpers";

import { useCustomerContext } from "../../context/CustomerContext";
import Form from "components/form/Form";
import FormLayout from "components/form/FormLayout";
import Input from "components/form/input/Input";
import { Select } from "components/select/index";
import { ButtonNew as Button } from "components/buttonNew";
import { Badge } from "components/badge/Badge";
import Popup from "components/popup/Popup";
import DatePickerInput from "components/datePickerInput/DatePickerInput";
import Page from "components/page/Page";
import Card from "components/card/Card";
import TagSelect from "components/tagSelect/TagSelect";
import { NavigationDropdownNew } from "components/navigationDropdownNew/NavigationDropdownNew";
import SelectNew from "components/select/SelectNew";
import CustomerAddressModal, { IAddressLineState } from "elements/customer/CustomerAddressModal";
import ExistingCustomers from "components/existingCustomers/ExistingCustomers";
import Portal from "elements/Portal";
import EmergencyContactCard from "./components/EmergencyContactCard";

import "./CustomerProfile.scss";
import { GetCustomerType } from "api/rpc/2024-04/facilityAdmin/customer/type";
import { postcodeValidator, postcodeValidatorExistsForCountry } from "postcode-validator";
import Toggle from "components/form/toggle/Toggle";
import Divider from "components/divider";
import useFutureBookingPricingModal from "../../futureBookingPricingModal/useFutureBookingPricingModal";

interface ICustomerTagState {
  tags: any[];
  attachedTags: any[];
  fullTagList: any[];
}

interface IModifiedCustomer {
  id?: boolean;
  first_name?: boolean;
  last_name?: boolean;
  email?: boolean;
  phone?: boolean;
  customer_type_id?: boolean;
  cart_customer_type_id?: boolean;
  member_code?: boolean;
  username?: boolean;
  birth_date?: boolean;
  bag_number?: boolean;
  locker_number?: string;
  home_facility_id?: boolean;
}

interface IMergeState {
  //Merge Modal
  selectedCustomer: ICustomer;

  //Warning Popup
  showWarning: boolean;

  // Customize merge
  bookings: boolean;
  memberships: boolean;
  tickets: boolean;
  house_accounts: boolean;
  gift_cards: boolean;
  orders: boolean;
  credit_books: boolean;
  loyalty_points: boolean;
  notes: boolean;
  playing_partners: boolean;
  reservations: boolean;
}

export default function CustomerProfile() {
  const { t } = useTranslation();
  const { Option } = Select;
  const { facilityStore, authStore } = useAppSelector(store => store);
  const history = useHistory();

  const windowSize = useWindowSize();

  const dispatch = useAppDispatch();

  const { state, updateState, loadCustomer } = useCustomerContext(); // changes seen across customer context
  const { openIfNeeded: openFutureBookingPricingModalIfNeeded, modal: futureBookingPricingModal } =
    useFutureBookingPricingModal();

  const [deleteAddressConfirmation, setDeleteAddressConfirmation] = useState({
    visible: false,
    address: null,
  });

  const [addressModalOpen, setAddressModalOpen] = useState(false);
  const [birthdayRequestVisible, setBirthdayRequestVisible] = useState(false);
  const [customerProfileStateBeforeChanges, setCustomerProfileStateBeforeChanges] = useState<ICustomer>(state.customer);
  const [customerProfileLoaded, setCustomerProfileLoaded] = useState<boolean>(false);

  const [mergeState, setMergeState] = useState<IMergeState>({
    selectedCustomer: null,
    showWarning: false,

    bookings: true,
    memberships: true,
    tickets: true,
    house_accounts: true,
    gift_cards: true,
    orders: true,
    credit_books: true,
    loyalty_points: true,
    notes: true,
    playing_partners: true,
    reservations: true,
});

  const [changedValues, setChangedValues] = useState<IModifiedCustomer>({
    id: false,
    first_name: false,
    last_name: false,
    email: false,
    phone: false,
    customer_type_id: false,
    cart_customer_type_id: false,
    member_code: false,
    username: false,
    birth_date: false,
    bag_number: false,
    locker_number: "",
    home_facility_id: false,
  });

  const [customerTagState, setCustomerTagState] = useState<ICustomerTagState>({
    tags: [],
    attachedTags: [],
    fullTagList: [],
  });

  const { state: existingCustomerModal, updateModal: updateExistingCustomerModal } = useModal({
    existingCustomers: [] as ICustomer[],
    popupText: "" as string,
  });

  // Populate Profile Dropdowns
  useEffect(() => {
    const source = axios.CancelToken.source();

    if (state.countries === null) {
      void GetCountries(source.token);
    }

    if (state.customerTypes === null) {
      void GetCustomerTypes(source.token);
    }

    if (state.cartTypes === null) {
      void GetCustomerCartTypes(source.token);
    }

    if (state.notes === null) {
      void loadCustomerNotes(state.customer.id);
    }

    setCustomerProfileLoaded(true);

    // Reset inputs when tab changes
    if (JSON.stringify(state.customer) != JSON.stringify(state.editState)) {
      updateState({ editState: { ...state.customer } });
    }

    return () => {
      source.cancel();
    };
  }, [state.customer.id]);

  useEffect(() => {
    setCustomerProfileStateBeforeChanges(state?.customer);
  }, [state?.customer]);

  useEffect(() => {
    const source = axios.CancelToken.source();
    void loadTags(source.token);
    return () => source.cancel();
  }, []);

  useEffect(() => {
    setCustomerProfileStateBeforeChanges({ ...state?.customer });
  }, [state?.customer?.banned, state?.customer?.banned_reason]);

  async function loadTags(token: CancelToken) {
    const getTagRes = await GetTag({ type: "customer" }, token ? false : true, token);

    if (token && token.reason) {
      return;
    }
    if (getTagRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error getting tags")));
      return;
    }

    const fullTagList = getTagRes.data.slice();

    const params = {
      customer_id: state.customer.id,
    };

    const getCustomerTagRes = await GetCustomerTag(params, token ? false : true, token);
    if (token && token.reason) {
      return;
    }
    if (getCustomerTagRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error getting customer tags")));
      return;
    }

    //filter attached tags from tag list
    for (let i = 0; i < getTagRes?.data.length; i++) {
      getTagRes.data[i].checked = false;

      for (let k = 0; k < getCustomerTagRes?.data?.length; k++) {
        if (getTagRes?.data[i]?.id === getCustomerTagRes?.data[k]?.id) {
          getTagRes?.data?.splice(i, 1);
        }
      }
    }

    setCustomerTagState(prevState => ({
      ...prevState,
      tags: getTagRes.data,
      attachedTags: getCustomerTagRes.data,
      fullTagList: fullTagList,
    }));
  }

  // Save EditState and update context state
  async function saveCustomer() {
    const customerTypeChanged =
      state.editState.customer_type_id !== customerProfileStateBeforeChanges.customer_type_id;
    const cartCustomerTypeChanged =
      state.editState.cart_customer_type_id !== customerProfileStateBeforeChanges.cart_customer_type_id;

    if (customerTypeChanged) {
      setChangedValues(() => ({
        customer_type_id: true,
      }));
    }
    if (state.editState.cart_customer_type_id !== customerProfileStateBeforeChanges.cart_customer_type_id) {
      setChangedValues(() => ({
        cart_customer_type_id: true,
      }));
    }

    const customerRes = await PutCustomer(
      {
        id: state.editState.id,
        first_name: state.editState.first_name,
        last_name: state.editState.last_name,
        email: state.editState.email,
        phone: state.editState.phone,
        customer_type_id: state.editState.customer_type_id,
        cart_customer_type_id: state.editState.cart_customer_type_id,
        member_code: state.editState.member_code,
        username: state.editState.username,
        birth_date: state.editState.birth_date,
        bag_number: state.editState.bag_number,
        locker_number: state.editState.locker_number,
        golf_canada_number: state.editState.golf_canada_number,
        ghin_number: state.editState.ghin_number,
        home_facility_id: state.editState.home_facility_id,
        updated_values: changedValues,
        tax_exempt: state.editState.tax_exempt,
        tax_exemption_number: state.editState.tax_exemption_number,
      },
      true,
    );

    // Handle if customer already exists
    if (customerRes.status === StatusCode.BAD_REQUEST) {
      const message = customerRes.data;

      const existingCustomerRes = await GetCustomerAll(
        {
          email: message.includes("phone") ? undefined : state.editState.email,
          phone: message.includes("phone") ? state.editState.phone : undefined,
          full_name: message.includes("phone")
            ? `${state.editState.first_name} ${state.editState.last_name}`
            : undefined,
        },
        true,
      );

      if (existingCustomerRes.status !== StatusCode.OK) {
        dispatch(showError(t("Cannot load staff account")));
        return;
      }

      const customers = existingCustomerRes?.data?.filter((customer: ICustomer) => customer.id !== state.customer.id);

      updateState({ editState: { ...state.editState, ...customerProfileStateBeforeChanges } });
      updateExistingCustomerModal({
        isOpen: true,
        existingCustomers: customers,
      });

      return;
    }

    if (
      !isEqualWith(state.customer?.address_lines, state.editState?.address_lines, (originalValue, newValue) => {
        if ((isNull(originalValue) || originalValue === "") && (isNull(newValue) || newValue === "")) {
          return true;
        }
      }) &&
      state.editState
    ) {
      for (const addressLine of state.editState.address_lines || []) {
        const countryCode =
          state.countries.find(country => country.id === addressLine.country_id)?.code ?? addressLine.country_code;
        const postalCode = (addressLine.postal ?? "").trim();
        if (postalCode !== "" && postcodeValidatorExistsForCountry(countryCode)) {
          if (!postcodeValidator(postalCode, countryCode)) {
            dispatch(showError(t("Incorrect postal code format")));
            return;
          }
        }
        const addressLineRes = await PutCustomerAddressLine(
          {
            id: addressLine?.id,
            customer_id: state.customer?.id,
            country_id: addressLine?.country_id,
            province_id: addressLine?.province_id,
            city: addressLine?.city,
            default: addressLine?.default,
            address_line_1: addressLine?.address_line_1,
            address_line_2: addressLine?.address_line_2,
            postal: addressLine?.postal,
            phone: addressLine?.phone,
          },
          true,
        );
        if (addressLineRes.status !== StatusCode.OK) {
          dispatch(showError(t("Error updating address line")));
          return;
        }
      }
    }

    if (customerRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error updating customer profile")));
      return;
    }

    dispatch(showSuccess(t("Successfully updated customer profile")));

    setChangedValues({
      id: false,
      first_name: false,
      last_name: false,
      email: false,
      phone: false,
      customer_type_id: false,
      cart_customer_type_id: false,
      member_code: false,
      username: false,
      birth_date: false,
      bag_number: false,
      locker_number: "",
      home_facility_id: false,
    });

    const nextCustomer: ICustomer = {
      ...state.customer,
      ...customerRes.data,
      address_lines: state.editState.address_lines,
    };

    const nextEditState: ICustomer = {
      ...state.editState,
      ...customerRes.data,
      address_lines: state.editState.address_lines,
    };

    updateState({
      customer: nextCustomer,
      editState: nextEditState,
    });

    setCustomerProfileStateBeforeChanges(nextEditState);

    if (
      (customerTypeChanged && nextEditState.customer_type_id) ||
      (cartCustomerTypeChanged && nextEditState.cart_customer_type_id)
    ) {
      await openFutureBookingPricingModalIfNeeded({
        customerId: nextCustomer.id,
        customerTypeId: customerTypeChanged ? nextEditState.customer_type_id ?? undefined : undefined,
        cartCustomerTypeId: cartCustomerTypeChanged ? nextEditState.cart_customer_type_id ?? undefined : undefined,
      });
    }
  }

  async function loadExistingCustomer(customerId: number) {
    if (!customerId) {
      return;
    }

    const customerRes = await PostExistingCustomer({ customer_id: customerId }, true);

    if (customerRes?.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading customer")));
      return;
    }

    dispatch(showSuccess(t("Customer loaded successfully")));
    await loadCustomer(Number(customerRes.data?.id));
    await loadCustomer(customerRes.data?.id as number);
  }

  async function GetCountries(token: CancelToken) {
    const countryRes = await GetCountry({ provinces: true }, false, token);

    if (token.reason) {
      return;
    }

    if (countryRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error getting countries")));
      return;
    }

    updateState({ countries: countryRes.data });
  }

  async function GetCustomerTypes(token: CancelToken) {
    const typesRes = await GetCustomerType(
      {
        application: "green_fee",
        limit: 1000,
      },
      false,
      token,
    );

    if (token.reason) {
      return;
    }

    if (typesRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error getting customer types")));
      return;
    }

    updateState({ customerTypes: typesRes.data });
  }

  async function GetCustomerCartTypes(token: CancelToken) {
    const typesRes = await GetCustomerType(
      {
        application: "power_cart",
      },
      token ? false : true,
      token,
    );

    if (token.reason) {
      return;
    }

    if (typesRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error getting cart types")));
      return;
    }

    updateState({ cartTypes: typesRes.data });
  }

  async function addAddressLine(addressLineState: IAddressLineState) {
    const countryCode = state.countries.find(country => country.id === addressLineState.country_id)?.code;

    if (addressLineState?.postal !== "") {
      if (postcodeValidatorExistsForCountry(countryCode)) {
        if (!postcodeValidator(addressLineState.postal, countryCode)) {
          dispatch(showError(t("Incorrect postal code format")));
          return;
        }
      }
    }

    const addressLineRes = await PostCustomerAddressLine(
      {
        customer_id: state.customer.id,
        country_id: addressLineState?.country_id,
        province_id: addressLineState?.province_id,
        city: addressLineState?.city,
        default: addressLineState?.default,
        address_line_1: addressLineState?.addressLine1,
        address_line_2: addressLineState?.addressLine2,
        postal: addressLineState?.postal,
        phone: addressLineState?.phone,
      },
      true,
    );

    if (addressLineRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error adding address line")));
      return;
    }

    const mergedEditState : ICustomer = {
      ...state.editState,
      ...addressLineRes.data,

    };

    dispatch(showSuccess(t("Successfully added address line")));

    updateState({ editState: mergedEditState });
    setCustomerProfileStateBeforeChanges(mergedEditState);
    setAddressModalOpen(false);
  }

  async function handleDefaultAddress(addressLine: IAddress) {
    const addressLineRes = await PutCustomerAddressLine(
      {
        id: addressLine?.id,
        customer_id: state.customer?.id,
        country_id: addressLine?.country_id,
        province_id: addressLine?.province_id,
        city: addressLine?.city,
        default: !addressLine?.default,
        address_line_1: addressLine?.address_line_1,
        address_line_2: addressLine?.address_line_2,
        postal: addressLine?.postal,
        phone: addressLine?.phone,
      },
      true,
    );
    if (addressLineRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error updating address line")));
      return;
    }

    updateState({
      customer: { ...state.customer, ...addressLineRes.data },
      editState: { ...state.editState, ...addressLineRes.data },
    }); // Update UI
  }

  async function deleteCustomerAddress(addressLine: IAddress) {
    const res = await DeleteCustomerAddressLine(
      { customer_id: state.customer.id, address_line_id: addressLine.id },
      true,
    );

    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Error removing customer address.")));
      return;
    }

    updateState({
      customer: { ...state.customer, ...res.data },
      editState: { ...state.editState, ...res.data },
    });

    dispatch(showSuccess(t("Address has been successfully removed.")));
    setDeleteAddressConfirmation({ visible: false, address: null });
  }

  function addressLineInputHandler(event: React.ChangeEvent<HTMLInputElement>, index: number, id: string) {
    const { value } = event.target;
    const updatedAddressLines = state.editState.address_lines ? [...state.editState.address_lines] : [];
    updatedAddressLines[index] = { ...updatedAddressLines[index], [id]: value };

    updateState({
      editState: {
        ...state.editState,
        address_lines: updatedAddressLines,
      },
    });
  }

  function addressLineDropdownChange(value: number, id: string, index: number, extraValue?: Record<string, any>) {
    const updatedAddressLines = state.editState.address_lines ? [...state.editState.address_lines] : [];
    updatedAddressLines[index] = {
      ...updatedAddressLines[index],
      [id]: value,
      country_code: extraValue ? extraValue?.code : updatedAddressLines[index]?.country_code,
    };

    updateState({
      editState: {
        ...state.editState,
        address_lines: updatedAddressLines,
      },
    });
  }

  function inputHandler(event: React.ChangeEvent<HTMLInputElement>) {
    const { id, value } = event.target;
    updateState({ editState: { ...state.editState, [id]: value } });

    if (customerProfileStateBeforeChanges[id as keyof ICustomer] !== value) {
      setChangedValues((prev: IModifiedCustomer) => ({
        ...prev,
        [id]: true,
      }));
    } else {
      setChangedValues((prev: IModifiedCustomer) => ({
        ...prev,
        [id]: false,
      }));
    }
  }

  function handleHomeCourseSelect(selected_home_facility_id: number) {
    const updatedState = {
      ...state.editState,
      home_facility_id: selected_home_facility_id,
    };

    updateState({ editState: updatedState });

    if (customerProfileStateBeforeChanges.home_facility_id !== selected_home_facility_id) {
      setChangedValues((prev: IModifiedCustomer) => ({
        ...prev,
        home_facility_id: true,
      }));
    } else {
      setChangedValues((prev: IModifiedCustomer) => ({
        ...prev,
        home_facility_id: false,
      }));
    }
  }

  function renderSaveChanges(screenSize: "desktop" | "mobile") {
    return (
      <Button
        type="primary"
        onClick={() => void saveCustomer()}
        className={`customer-profile-save-changes-${screenSize}`}
      >
        {t("Save Changes")}
      </Button>
    );
  }

  function renderAddAddress(screenSize: "desktop" | "mobile") {
    const isDesktop = windowSize.width > MOBILE_WIDTH;

    const addAddress = (
      <Button
        className={`customer-profile-action customer-profile-add-address-${screenSize}`}
        size="medium"
        type="link"
        onClick={() => setAddressModalOpen(true)}
      >
        {t("+ Add Address")}
      </Button>
    );

    if (screenSize === "desktop" && isDesktop) {
      return <>{addAddress}</>;
    } else if (screenSize === "mobile" && !isDesktop) {
      return <FormLayout.Group>{addAddress}</FormLayout.Group>;
    } else {
      return <></>;
    }
  }

  async function handleSendBirthdayRequest() {
    if (!state.customer?.email) {
      dispatch(showError(t("This customer does not have an email address")));
      setBirthdayRequestVisible(false);
      return;
    }

    const response = await SendBirthdayRequestEmail({ email: state.customer.email }, true);

    if (response.status !== StatusCode.OK) {
      dispatch(showError(t("Error sending birthday request email")));
      return;
    }

    dispatch(showSuccess(t("Birthday request email sent")));
    setBirthdayRequestVisible(false);
  }

  function handleDateChange(selectedDate: Date) {
    const updatedState = {
      ...state.editState,
      birth_date: selectedDate,
    };

    updateState({ editState: updatedState });
  }

  async function loadCustomerNotes(customerId: number, globalLoader?: boolean, token?: CancelToken) {
    const customerNotesRes = await GetCustomerNotes(
      {
        customer_id: customerId,
      },
      globalLoader,
      token,
    );

    if (customerNotesRes.status !== StatusCode.OK) {
      return;
    }

    updateState({ notes: customerNotesRes.data });
  }

  async function loadAndSyncCustomer() {
    await loadCustomer(state?.customer?.id);
  }

  function closeMergeAccounts() {
    setMergeState(prevState => ({
      ...prevState,
      selectedCustomer: null,
      showWarning: false,
      bookings: true,
      memberships: true,
      tickets: true,
      house_accounts: true,
      gift_cards: true,
      orders: true,
      credit_books: true,
      loyalty_points: true,
      notes: true,
      playing_partners: true,
      reservations: true,
    }));
  }

  async function handleCustomerMerge() {
    const mergeRes = await PutMergeCustomers(
      { 
        parent_id: state?.customer?.id, 
        child_id: mergeState?.selectedCustomer?.id,
        bookings: mergeState?.bookings,
        orders: mergeState?.orders,
        memberships: mergeState?.memberships,
        gift_cards: mergeState?.gift_cards,
        tickets: mergeState?.tickets,
        notes: mergeState?.notes,
        playing_partners: mergeState?.playing_partners,
        loyalty_points: mergeState?.loyalty_points,
        credit_books: mergeState?.credit_books,
        house_accounts: mergeState?.house_accounts,
        reservations: mergeState?.reservations,
      },
      true,
    );
    if (mergeRes?.status !== StatusCode.OK) {
      dispatch(showError(t("Error merging customer accounts")));
      return;
    }
    dispatch(showSuccess(t("Successfully merged customer accounts")));
    void closeMergeAccounts();
    void updateExistingCustomerModal({ isOpen: false, existingCustomers: [] });
    await loadCustomer(state?.customer?.id);
  }

  return (
    <>
      <Page
        splitLayout
        notificationBarProps={{
          isVisible: unsavedChangesExist(customerProfileStateBeforeChanges, state.editState),
          onAction: () => void saveCustomer(),
          onCancel: () => updateState({ editState: { ...state.editState, ...customerProfileStateBeforeChanges } }),
        }}
      >
        <Page.Section twoThirds>
          <div className="mb-5">
            <span className="flex mt-2">
              <h1 className="ui-customer_page-header">{t("Basic Information")}</h1>

              <div>
                <NavigationDropdownNew
                  label={t("Options")}
                  rightAlign
                  sections={[
                    [
                      {
                        icon: "plus",
                        label: t("Add Address"),
                        onClick: () => setAddressModalOpen(true),
                      },
                    ],
                  ]}
                />
              </div>

              {/* {renderCustomerPreferences("desktop")} */}
              {/* {renderSaveChanges("desktop")} */}
            </span>
            <hr className="customer-profile-header-divider" />
            <Form>
              <FormLayout>
                <FormLayout.Group>
                  <Input
                    value={state.editState.first_name || ""}
                    label={t("First Name")}
                    id="first_name"
                    onChange={inputHandler}
                  />
                  <Input
                    value={state.editState.last_name || ""}
                    label={t("Last Name")}
                    id="last_name"
                    onChange={inputHandler}
                  />
                </FormLayout.Group>

                <FormLayout.Group>
                  <Input
                    value={state.editState.phone || ""}
                    label={t("Phone Number")}
                    id="phone"
                    onChange={inputHandler}
                  />
                  <Input value={state.editState.email || ""} label={t("Email")} id="email" onChange={inputHandler} />
                </FormLayout.Group>

                <div className="customer-profile-mobile-adaptive-fields">
                  <Input
                    value={state.editState?.username || ""}
                    label={t("Username")}
                    id="username"
                    onChange={inputHandler}
                  />
                  <div className="password-input">
                    <Input
                      value={"**********"}
                      type="password"
                      autoComplete="off"
                      label={t("Password")}
                      disabled={true}
                      className="password-input-input"
                    />
                  </div>
                </div>

                {renderAddAddress("mobile")}

                <div className="customer-profile-mobile-adaptive-fields-triple">
                  <SelectNew
                    id={"customer_type_id"}
                    value={state.editState.customer_type_id}
                    onChange={value => {
                      updateState({ editState: { ...state.editState, customer_type_id: value } });
                      if (value !== customerProfileStateBeforeChanges.customer_type_id) {
                        setChangedValues((prev: IModifiedCustomer) => ({
                          ...prev,
                          customer_type_id: true,
                        }));
                      } else {
                        setChangedValues((prev: IModifiedCustomer) => ({
                          ...prev,
                          customer_type_id: false,
                        }));
                      }
                    }}
                    label={t("Customer Type")}
                    isLoading={state.customerTypes === null}
                    options={state.customerTypes?.map(type => ({
                      id: type.id,
                      label: type.full_title,
                    }))}
                    searchable
                    placeholder={t("Search Customer Types..")}
                    disabled={!authStore?.user?.permissions?.user_edit_customer_type}
                    // onClear={() => updateState({ editState: { ...state.editState, customer_type_id: undefined}})}
                  />

                  <SelectNew
                    id={"customer_cart_type"}
                    value={state.editState.cart_customer_type_id}
                    onChange={value => {
                      updateState({ editState: { ...state.editState, cart_customer_type_id: value } });
                      if (value !== customerProfileStateBeforeChanges.cart_customer_type_id) {
                        setChangedValues((prev: IModifiedCustomer) => ({
                          ...prev,
                          cart_customer_type_id: true,
                        }));
                      } else {
                        setChangedValues((prev: IModifiedCustomer) => ({
                          ...prev,
                          cart_customer_type_id: false,
                        }));
                      }
                    }}
                    label={t("Cart Type")}
                    isLoading={state.cartTypes === null}
                    options={state.cartTypes?.map(type => ({
                      id: type.id,
                      label: type.full_title,
                    }))}
                    searchable
                    placeholder={t("Search Cart Types..")}
                    disabled={!authStore?.user?.permissions?.user_edit_customer_type}
                    // onClear={() => updateState({ editState: { ...state.editState, cart_customer_type_id: undefined}})}
                  />

                  {state.editState?.birth_date ? (
                    <div className="birthday-input">
                      <DatePickerInput
                        months={1}
                        label={t("Birthday")}
                        startingDate={new Date(state.editState?.birth_date as unknown as string)}
                        setStartingDate={(e: Date) => handleDateChange(e)}
                      />
                      <div className="birthday-cancel">
                        <FontAwesomeIcon
                          className="birthday-cancel-icon"
                          icon={faTimes}
                          onClick={() => {
                            updateState({ editState: { ...state.editState, birth_date: null } });
                            if (customerProfileStateBeforeChanges.birth_date !== null) {
                              setChangedValues((prev: IModifiedCustomer) => ({
                                ...prev,
                                birth_date: true,
                              }));
                            } else {
                              setChangedValues((prev: IModifiedCustomer) => ({
                                ...prev,
                                birth_date: false,
                              }));
                            }
                          }}
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="birthday-toggle">
                      <Button
                        onClick={() => {
                          updateState({
                            editState: { ...state.editState, birth_date: state.customer.birth_date ?? new Date() },
                          });
                          if (customerProfileStateBeforeChanges.birth_date !== null) {
                            setChangedValues((prev: IModifiedCustomer) => ({
                              ...prev,
                              birth_date: true,
                            }));
                          } else {
                            setChangedValues((prev: IModifiedCustomer) => ({
                              ...prev,
                              birth_date: false,
                            }));
                          }
                        }}
                      >
                        {t("Set Birthday")}
                      </Button>
                      <Button type="secondary" onClick={() => setBirthdayRequestVisible(true)}>
                        {t("Request Birthday")}
                      </Button>
                    </div>
                  )}
                </div>

                <FormLayout.Group>
                  <Input
                    value={state.editState.member_code || ""}
                    label={t("Member #")}
                    id="member_code"
                    onChange={inputHandler}
                    placeholder=""
                  />
                  <Input
                    label={t("Bag #")}
                    id="bag_number"
                    placeholder="513"
                    onChange={inputHandler}
                    value={state.editState.bag_number || ""}
                  />
                  <Input
                    label={t("Locker #")}
                    id="locker_number"
                    placeholder="628"
                    onChange={inputHandler}
                    value={state.editState.locker_number || ""}
                  />
                  {facilityStore.facility?.country_code === "CA" && (
                    <Input
                      label={t("Golf Canada #")}
                      id="golf_canada_number"
                      placeholder=""
                      onChange={inputHandler}
                      value={state.editState.golf_canada_number || ""}
                    />
                  )}
                  {(facilityStore.facility?.country_code === "US" ||
                    facilityStore.facility?.country_code === "DO") && (
                    <Input
                      label={t("GHIN #")}
                      id="ghin_number"
                      placeholder=""
                      onChange={inputHandler}
                      value={state.editState.ghin_number || ""}
                    />
                  )}
                </FormLayout.Group>

                <FormLayout.Group>
                  <Select
                    label={t("Home Course")}
                    defaultValue={state.customer?.home_facility_id === null ? null : state.customer?.home_facility_id}
                    onChange={(selected_course_id: number) => handleHomeCourseSelect(selected_course_id)}
                  >
                    {facilityStore.facilities.map(facility => {
                      return (
                        <Option key={facility.id} value={facility.id} name={facility.full_name}>
                          <span>{facility.long_name}</span>
                        </Option>
                      );
                    })}
                  </Select>
                </FormLayout.Group>

                <FormLayout.Group>
                  <Toggle
                    label={t("Tax Exempt")}
                    checked={state.editState.tax_exempt}
                    onChange={e =>
                      updateState({
                        editState: { ...state.editState, tax_exempt: e.target.checked },
                      })
                    }
                  />
                  {state.editState.tax_exempt ? (
                    <Input
                      label={t("Tax Exemption Number")}
                      id="tax_exemption_number"
                      placeholder=""
                      onChange={inputHandler}
                      value={state.editState.tax_exemption_number || ""}
                    />
                  ) : null}
                </FormLayout.Group>
              </FormLayout>
            </Form>

            <Divider />

            {state.editState?.address_lines?.map((addressLine, index) => {
              return (
                <React.Fragment key={index}>
                  <div className="customer-default-address-container">
                    <div>
                      <h1 style={{ display: "inline" }} className="ui-customer_page-header">{`Address ${
                        index + 1
                      }`}</h1>
                      {addressLine?.default && (
                        <Badge size="medium" type="gray">
                          {t("Default")}
                        </Badge>
                      )}
                    </div>
                    <div className="ui-customer_address-actions">
                      <div>
                        <Button size="medium" type="link" onClick={() => void handleDefaultAddress(addressLine)}>
                          {!addressLine?.default ? t("Set as Default") : t("Remove as Default")}
                        </Button>
                      </div>
                      <Button
                        size="medium"
                        type="link"
                        onClick={() => setDeleteAddressConfirmation({ visible: true, address: addressLine })}
                      >
                        {t("Remove")}
                      </Button>
                    </div>
                  </div>

                  <Form>
                    <FormLayout>
                      <FormLayout.Group>
                        <Input
                          value={addressLine?.address_line_1 || ""}
                          label={t("Address Line 1")}
                          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                            addressLineInputHandler(event, index, "address_line_1")
                          }
                        />

                        <Input
                          value={addressLine?.address_line_2 || ""}
                          label={t("Address Line 2")}
                          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                            addressLineInputHandler(event, index, "address_line_2")
                          }
                        />
                      </FormLayout.Group>

                      <FormLayout.Group>
                        <Input
                          value={addressLine?.city || ""}
                          label={t("City")}
                          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                            addressLineInputHandler(event, index, "city")
                          }
                        />

                        <Input
                          value={addressLine?.postal || ""}
                          label={t("Postal Code")}
                          error={(() => {
                            const trimmedPostal = (addressLine?.postal ?? "").trim();
                            if (trimmedPostal.length === 0) {
                              return false;
                            }

                            const countryCode =
                              state?.countries?.find(country => country.id === addressLine.country_id)?.code ??
                              addressLine.country_code ??
                              "";

                            if (!postcodeValidatorExistsForCountry(countryCode)) {
                              return false;
                            }

                            return !postcodeValidator(trimmedPostal, countryCode);
                          })()}
                          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                            addressLineInputHandler(event, index, "postal")
                          }
                        />

                        <Input
                          value={addressLine?.phone || ""}
                          label={t("Phone Number")}
                          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                            addressLineInputHandler(event, index, "phone")
                          }
                        />
                      </FormLayout.Group>

                      <FormLayout.Group>
                        {state.countries && (
                          <Select
                            label={t("Country")}
                            onChange={(value: number, extraValues: Record<string, any>) =>
                              addressLineDropdownChange(value, "country_id", index, extraValues)
                            }
                            defaultValue={addressLine?.country_id}
                          >
                            {state?.countries?.map((country, index) => {
                              return (
                                <Option key={index} value={country?.id} name={country?.name} extraValues={country}>
                                  {country?.name}
                                </Option>
                              );
                            })}
                          </Select>
                        )}

                        {state?.countries?.some(country => country?.id === addressLine?.country_id) && (
                          <Select
                            label={t("Province")}
                            onChange={(value: number) => addressLineDropdownChange(value, "province_id", index)}
                            defaultValue={addressLine?.province_id}
                          >
                            {state.countries
                              ?.find(country => country?.id === addressLine?.country_id)
                              ?.provinces?.map((province: Record<string, any>, index: number) => {
                                return (
                                  <Option key={index} value={province?.id} name={province?.name}>
                                    {province?.name}
                                  </Option>
                                );
                              })}
                          </Select>
                        )}
                      </FormLayout.Group>
                    </FormLayout>
                  </Form>
                </React.Fragment>
              );
            })}

            {customerProfileLoaded ? <EmergencyContactCard onRefresh={() => void loadAndSyncCustomer()} /> : null}

            <div className="customer-profile-actions">{/* <div>{renderAddAddress("desktop")}</div> */}</div>
            <div className="customer-profile-delete-customer">{renderSaveChanges("mobile")}</div>
          </div>
        </Page.Section>

        <Page.Section oneThird>
          <div className="ml-5">
            <Card>
              <Card.Section>
                <TagSelect
                  label={t("Tags")}
                  tags={customerTagState.tags}
                  attachedTags={customerTagState.attachedTags}
                  fullTagList={customerTagState.fullTagList}
                  footerLabel={t("Manage tags")}
                  footerAction={() => history.push("/admin/settings/customer_tags")}
                  footerIcon={faPencil}
                />
              </Card.Section>
            </Card>
          </div>
        </Page.Section>
      </Page>

      {addressModalOpen && (
        <CustomerAddressModal
          isVisible={addressModalOpen}
          countries={state.countries}
          onCancel={() => void setAddressModalOpen(false)}
          onOk={addressLine => void addAddressLine(addressLine)}
        />
      )}

      <Popup
        open={birthdayRequestVisible}
        title={t("Request Birthday")}
        type="info"
        description={t("An email will be sent asking this customer to add their birthday and update their profile.")}
        onCancel={() => setBirthdayRequestVisible(false)}
        onOk={handleSendBirthdayRequest}
        okText={t("Send")}
      />

      {/* DELETE ADDRESS POPUP */}
      <Popup
        open={deleteAddressConfirmation.visible}
        description={`${t("Are you sure you want to remove this address from")} ${state.customer.full_name} ${t(
          "'s profile?",
        )}`}
        title={t("Delete Customer Address")}
        type="warning"
        onCancel={() => setDeleteAddressConfirmation({ visible: false, address: null })}
        onOk={() => deleteCustomerAddress(deleteAddressConfirmation.address as IAddress)}
      />

      {/* EXISTING CUSTOMERS POPUP */}
      <Portal isMounted={existingCustomerModal.isOpen}>
        <ExistingCustomers
          open={existingCustomerModal.isOpen}
          customers={existingCustomerModal.existingCustomers}
          onClose={() => void updateExistingCustomerModal({ isOpen: false, existingCustomers: [] })}
          onLoadCustomer={customerId => void loadExistingCustomer(customerId)}
          onMergeOpen={customer => setMergeState(prev => ({...prev, selectedCustomer: customer, showWarning: true}))}
        />
      </Portal>

      <Portal>
      <Popup
        open={mergeState?.showWarning}
        type={"warning"}
        stacked
        title={t("Merge Customers?")}
        onCancel={() => setMergeState(prevState => ({ ...prevState, 
          showWarning: false, 
          bookings: true,
          memberships: true,
          tickets: true,
          house_accounts: true,
          gift_cards: true,
          orders: true,
          credit_books: true,
          loyalty_points: true,
          notes: true,
          playing_partners: true,
          reservations: true,
          selectedCustomer: null,
        }))}
        noFixedWidth
        cancelText={t("Cancel")}
        okText={t("Merge")}
        onOk={handleCustomerMerge}
        description={`${t("The account of")} ${
          mergeState?.selectedCustomer?.first_name
        } ${mergeState?.selectedCustomer?.last_name} ${t("will be merged into the account of")}
        ${state?.customer?.full_name}. ${t("The selected options will be merged. This action cannot be undone. Are you sure you want to continue with this merge?")}
        `}
      >
        <br />
        <FormLayout>
          <FormLayout.Group>
            <Toggle checked={mergeState?.bookings} onChange={e => setMergeState(prev => ({...prev, bookings: e.target.checked}))} labelRight={t("Bookings")} />
            <Toggle checked={mergeState?.memberships} onChange={e => setMergeState(prev => ({...prev, memberships: e.target.checked}))} labelRight={t("Memberships")} />
          </FormLayout.Group>
          <FormLayout.Group>
            <Toggle checked={mergeState?.tickets} onChange={e => setMergeState(prev => ({...prev, tickets: e.target.checked}))} labelRight={t("Tickets")} />
            <Toggle checked={mergeState?.house_accounts} onChange={e => setMergeState(prev => ({...prev, house_accounts: e.target.checked}))} labelRight={t("House accounts")} />
          </FormLayout.Group>
          <FormLayout.Group>
            <Toggle checked={mergeState?.gift_cards} onChange={e => setMergeState(prev => ({...prev, gift_cards: e.target.checked}))} labelRight={t("Gift cards")} />
            <Toggle checked={mergeState?.orders} onChange={e => setMergeState(prev => ({...prev, orders: e.target.checked}))} labelRight={t("Orders")} />
          </FormLayout.Group>
          <FormLayout.Group>
            <Toggle checked={mergeState?.credit_books} onChange={e => setMergeState(prev => ({...prev, credit_books: e.target.checked}))} labelRight={t("Credit books")} />
            <Toggle checked={mergeState?.loyalty_points} onChange={e => setMergeState(prev => ({...prev, loyalty_points: e.target.checked}))} labelRight={t("Loyalty points")} />
          </FormLayout.Group>
          <FormLayout.Group>
            <Toggle checked={mergeState?.notes} onChange={e => setMergeState(prev => ({...prev, notes: e.target.checked}))} labelRight={t("Notes")} />
            <Toggle checked={mergeState?.playing_partners} onChange={e => setMergeState(prev => ({...prev, playing_partners: e.target.checked}))} labelRight={t("Playing partners")} />
          </FormLayout.Group>
          <FormLayout.Group>
            <Toggle checked={mergeState?.reservations} onChange={e => setMergeState(prev => ({...prev, reservations: e.target.checked}))} labelRight={t("Reservations")} />
          </FormLayout.Group>
        </FormLayout>
          </Popup>
      </Portal>
      {futureBookingPricingModal}
    </>
  );
}
